import React, { useState } from "react";
import { ArrowRight, Clock, MapPin, Utensils, Check, Bed, Sparkles } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { buildWhatsAppLink, PACKAGE_STATIC, FLEET_STATIC } from "../data/static";
import { formatPrice } from "../utils/currency";

const TagPill = ({ tag }) => (
  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FDE68A] text-[#7C4A00] text-[10px] font-semibold tracking-wider uppercase">
    <Sparkles className="w-3 h-3" /> {tag}
  </span>
);

const PackageCard = ({ pkg, index, p }) => {
  const stat = PACKAGE_STATIC[pkg.id];
  const merged = stat.pricing.map((row, i) => ({
    ...row,
    label: pkg.priceLabels?.[i] || "",
  }));
  const [selectedVehicle, setSelectedVehicle] = useState(merged[0]?.vehicle || "");
  const selected = merged.find((r) => r.vehicle === selectedVehicle) || merged[0];
  const link = buildWhatsAppLink(p.waMsg(pkg.title, selected?.vehicle || ""));
  const isEven = index % 2 === 0;

  return (
    <article data-testid={`package-card-${pkg.id}`}
      className="group bg-white rounded-3xl overflow-hidden border border-slate-200/80 hover:shadow-2xl hover:shadow-slate-900/5 transition-all duration-500 animate-fade-in-up"
      style={{ animationDelay: `${index * 100}ms` }}>
      <div className={`flex flex-col lg:flex-row ${isEven ? "" : "lg:flex-row-reverse"}`}>
        <div className="relative lg:w-2/5 aspect-[4/3] lg:aspect-auto overflow-hidden bg-slate-100">
          {stat.heroImagesData && pkg.heroImageLabels ? (
            <div className="absolute inset-0 grid grid-rows-3 gap-1 bg-slate-200">
              {stat.heroImagesData.map((img) => (
                <div key={img.key} className="relative overflow-hidden group/triptych">
                  <img src={img.src} alt={pkg.heroImageLabels[img.key]}
                    className="w-full h-full object-cover group-hover/triptych:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-r from-slate-900/60 via-slate-900/15 to-transparent" />
                  <div className="absolute inset-y-0 left-0 flex items-center px-4">
                    <span className="font-display text-sm sm:text-base lg:text-lg font-semibold text-white drop-shadow leading-tight">
                      {pkg.heroImageLabels[img.key]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <img src={stat.heroImage} alt={pkg.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
          )}
          <div className="absolute top-5 left-5 flex items-center gap-2 z-10">
            <TagPill tag={pkg.tag} />
          </div>
          <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-white z-10">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium drop-shadow">{pkg.duration}</span>
          </div>
          {!stat.heroImagesData && (
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
          )}
        </div>

        <div className="lg:w-3/5 p-6 sm:p-8 lg:p-10 flex flex-col">
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight">{pkg.title}</h3>
          <p className="mt-2 text-base text-slate-600 leading-relaxed">{pkg.summary}</p>

          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#D1603D] mb-3">{p.itineraryHeader}</div>
            {pkg.itineraryByDay ? (
              <div className="space-y-5">
                {pkg.itineraryByDay.map((group, gi) => (
                  <div key={group.day}>
                    <div className="flex items-center gap-3 mb-2.5">
                      <span className="w-7 h-7 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xs font-bold">
                        {gi + 1}
                      </span>
                      <div className="leading-tight">
                        <div className="text-sm font-display font-semibold text-slate-900">{group.day}</div>
                        {group.label && (
                          <div className="text-xs text-slate-500">{group.label}</div>
                        )}
                      </div>
                      <span className="flex-1 h-px bg-slate-200" />
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pl-10">
                      {group.stops.map((s) => (
                        <li key={s.place} className="flex items-start gap-2.5 text-sm">
                          <MapPin className="w-3.5 h-3.5 text-[#D1603D] mt-0.5 shrink-0" />
                          <div>
                            <span className="font-medium text-slate-800">{s.place}</span>
                            {s.note && <div className="text-xs text-slate-500">{s.note}</div>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {pkg.itinerary.map((it) => (
                  <li key={`${it.time}-${it.place}`} className="flex items-start gap-2.5 text-sm">
                    <MapPin className="w-3.5 h-3.5 text-[#D1603D] mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold text-slate-900">{it.time}</span>{" "}
                      <span className="text-slate-700">· {it.place}</span>
                      {it.note && <div className="text-xs text-slate-500">{it.note}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {pkg.foodStops && (
            <div className="mt-5 flex items-start gap-2 text-sm">
              <Utensils className="w-4 h-4 text-[#D1603D] mt-0.5 shrink-0" />
              <p className="text-slate-600">
                <span className="font-semibold text-slate-900">{p.foodStops}</span>{" "}
                {pkg.foodStops.join(", ")}
              </p>
            </div>
          )}

          {pkg.accommodation && (
            <div className="mt-3 flex items-start gap-2 text-sm">
              <Bed className="w-4 h-4 text-[#D1603D] mt-0.5 shrink-0" />
              <p className="text-slate-600">{pkg.accommodation}</p>
            </div>
          )}

          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">{p.includes}</div>
            <div className="flex flex-wrap gap-2">
              {pkg.inclusions.map((inc) => (
                <span key={inc} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F3F4F0] text-xs text-slate-700">
                  <Check className="w-3 h-3 text-emerald-600" />
                  {inc}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-7 pt-6 border-t border-slate-200">
            <div className="flex flex-wrap gap-2 mb-4">
              {merged.map((row) => {
                const fleetKey = row.vehicle.toLowerCase().replace(/\s/g, "-");
                const seats = FLEET_STATIC[fleetKey]?.seats;
                return (
                  <button key={row.vehicle}
                    data-testid={`pkg-${pkg.id}-vehicle-${fleetKey}`}
                    onClick={() => setSelectedVehicle(row.vehicle)}
                    className={`px-4 py-2 rounded-full text-xs transition-all flex flex-col items-center gap-0.5 ${
                      selectedVehicle === row.vehicle
                        ? "bg-slate-900 text-white"
                        : "bg-white border border-slate-200 text-slate-700 hover:border-slate-900"
                    }`}>
                    <span className="font-semibold">{row.vehicle}</span>
                    {seats && (
                      <span className={`text-[10px] ${selectedVehicle === row.vehicle ? 'text-slate-300' : 'text-slate-500'}`}>
                        {seats} Seater
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                  {selected?.label || p.totalPackage}
                </div>
                <div className="font-display text-3xl font-semibold text-slate-900">
                  {formatPrice(selected?.price)}
                </div>
                {pkg.notes && (
                  <p className="text-xs text-slate-500 mt-1.5 max-w-md leading-relaxed">{pkg.notes}</p>
                )}
              </div>
              <a href={link} target="_blank" rel="noopener noreferrer" data-testid={`pkg-book-${pkg.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D1603D] text-white text-sm font-semibold shadow-lg shadow-[#D1603D]/25 hover:bg-[#B34A2B] transition-colors active:scale-95">
                {p.bookNow} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export const TourPackages = () => {
  const { t } = useI18n();
  const p = t.packages;

  return (
    <section id="packages" data-testid="packages-section" className="relative py-20 lg:py-28 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D1603D]">{p.eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
            {p.title}
            <br className="hidden sm:block" />
            <span className="italic text-slate-500">{p.titleItalic}</span>
          </h2>
          <p className="mt-4 text-base text-slate-600">{p.subtitle}</p>
        </div>

        <div className="space-y-8 lg:space-y-12">
          {p.list.map((pkg, i) => (
            <PackageCard key={pkg.id} pkg={pkg} index={i} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TourPackages;
